// @flow
const EDIT_PAGE = 'EDIT_PAGE';
const REVERT_PAGE = 'REVERT_PAGE';
const APPROVE_PAGE = 'APPROVE_PAGE';
const DECLINE_PAGE = 'DECLINE_PAGE';
const REQUEST_APPROVAL = 'REQUEST_APPROVAL';
const ADD_CATEGORY = 'ADD_CATEGORY';
const COPY_PAGE = 'COPY_PAGE';
const REMOVE_PAGE = 'REMOVE_PAGE';
const DELETE_PAGE = 'DELETE_PAGE';
const AIXC_DEPLOY_ROLE = 'AIXC_DEPLOY';
const AIXC_PATH_PREFIX = '/cxc10';

type Action = 'EDIT' | 'APPROVE';

const normalizePath = (path) => (typeof path === 'string' ? path : '');

const getOthersRoles = (user) => {
  if (!user || !user.roles || !Array.isArray(user.roles.others)) {
    return [];
  }

  return user.roles.others;
};

export const isAixcPagePath = (path) => normalizePath(path).startsWith(AIXC_PATH_PREFIX);

export const hasPermissionAixcDeploy = (user) => {
  if (!user || !user.isActive) {
    return false;
  }

  if (user.isSuperAdmin) {
    return true;
  }

  return getOthersRoles(user).some(o => o.category?.toString() === AIXC_DEPLOY_ROLE);
};

const checkPermissions = ({ type, user, categoryId, action }) => {
  if (['EDIT', 'APPROVE'].indexOf(action) === -1) {
    throw new Error('Invalid action on checked permission.');
  }

  if (!user.isActive) {
    return false;
  }

  if (user.isSuperAdmin) {
    return true;
  }

  const requiredRole = action === 'EDIT' ? 'EDITOR' : 'APPROVER';

  const othersRoles = getOthersRoles(user);
  const hasBasicEditingPermission = othersRoles.some(o => o.category.toString() === 'BASIC' && o.roleType === requiredRole);

  // Permission based on category + role combination
  const hasCategoryPermission = categoryId && user.roles.categories.some(c =>
    c.category.toString() === categoryId.toString() && c.roleType === requiredRole);

  switch (type) {
    case EDIT_PAGE:
    case REVERT_PAGE:
    case REQUEST_APPROVAL:
    case APPROVE_PAGE:
    case DECLINE_PAGE:
      return hasCategoryPermission || hasBasicEditingPermission;
    case ADD_CATEGORY:
    case COPY_PAGE:
    case REMOVE_PAGE:
    case DELETE_PAGE:
      return hasCategoryPermission
    default:
      return false;
  }
}

export const hasPermissionEditPage = (user, categoryId, action: Action) => checkPermissions({ type: EDIT_PAGE, user, categoryId, action });
export const hasPermissionRevertPage = (user, categoryId, action: Action) => checkPermissions({ type: REVERT_PAGE, user, categoryId, action });
export const hasPermissionRequestApproval = (user, categoryId, action: Action) => checkPermissions({ type: REQUEST_APPROVAL, user, categoryId, action });
export const hasPermissionApprovePage = (user, categoryId, action: Action) => checkPermissions({ type: APPROVE_PAGE, user, categoryId, action });
export const hasPermissionDeclinePage = (user, categoryId, action: Action) => checkPermissions({ type: DECLINE_PAGE, user, categoryId, action });

// Ignore 'BASIC' role (same as earlier 'hasPermission')
export const hasPermissionAddCategory = (user, categoryId, action: Action) => checkPermissions({ type: ADD_CATEGORY, user, categoryId, action });
export const hasPermissionCopyPage = (user, categoryId, action: Action) => checkPermissions({ type: COPY_PAGE, user, categoryId, action });
export const hasPermissionRemovePage = (user, categoryId, action: Action) => checkPermissions({ type: REMOVE_PAGE, user, categoryId, action });
export const hasPermissionDeletePage = (user, categoryId, action: Action) => checkPermissions({ type: DELETE_PAGE, user, categoryId, action });

export const canRequestApprovalOnAixcPage = (user, path) => hasPermissionAixcDeploy(user) && isAixcPagePath(path);
export const canApproveAixcPage = (user, path) => hasPermissionAixcDeploy(user) && isAixcPagePath(path);
